/**
 * SweepConfirmSheet — Plan 04-03 / UI-SPEC §"<SweepConfirmSheet />".
 *
 * Compact BottomSheet titled "Slette {N} hakede varer?" with two stacked
 * buttons: `Slett` (terracotta) and `Avbryt` (ghost). Default focus
 * `Avbryt` — the safer choice. `Slett` calls `sweep.mutate()` → server
 * soft-deletes all checked rows.
 *
 * A failing sweep reports inline via <SheetError>, never via a toast:
 * this sheet is at z-50 and ToastViewport at z-40 (Pattern 7 in
 * docs/ARCHITECTURE.md), so a toast would be painted behind the scrim and
 * the adult would see the sheet simply refuse to close (issue #78).
 *
 * data-testids: shopping-sweep-sheet (panel root), shopping-sweep-confirm
 * (Slett), shopping-sweep-cancel (Avbryt), shopping-sweep-error (inline
 * failure copy).
 *
 * #157 (redesign steg 7d, D9): fortsatt et ark. Knappene er Button —
 * Avbryt sekundær og Slett `danger`, begge 52 px i full bredde.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BottomSheet } from '../../components/primitives/BottomSheet';
import { Button } from '../../components/primitives/Button';
import { SheetError } from '../../components/primitives/SheetError';
import { useShoppingMutations } from './useShoppingMutations';

interface SweepConfirmSheetProps {
  open: boolean;
  count: number;
  onClose: () => void;
}

export function SweepConfirmSheet({ open, count, onClose }: SweepConfirmSheetProps) {
  const { t } = useTranslation();
  const { sweep } = useShoppingMutations();
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Default focus on Avbryt — the safer choice. BottomSheet auto-focuses
  // the first focusable child; we render Avbryt first so it's selected.
  //
  // Clearing the error on open matters here and not in every sheet:
  // ShoppingRoute mounts this one permanently (`open={sweepOpen}`), so the
  // state outlives a close. Without this, failing a sweep and then closing
  // and reopening the sheet greets the adult with the old message over a
  // list that may since have changed.
  useEffect(() => {
    if (open) {
      setError(null);
      cancelRef.current?.focus();
    }
  }, [open]);

  function onSubmit() {
    // Clear any stale message first — a second attempt must not sit under
    // the copy from the first one.
    setError(null);
    sweep.mutate(undefined, {
      onSuccess: () => onClose(),
      // Additive, not a replacement: React Query runs the hook-level
      // onError in useShoppingMutations too, and that one is what rolls the
      // optimistic cache back. Both are needed — this handler only surfaces
      // the failure, it restores nothing. Locked by "rolls the sweep back
      // AND still runs the caller onError" in
      // __tests__/useShoppingMutations.offline.test.tsx.
      onError: () => setError(t('errors.generic.body')),
    });
  }

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={t('shopping.sweep.confirm.title', { count })}
    >
      <div data-testid="shopping-sweep-sheet" className="flex flex-col gap-3">
        <p className="m-0 text-body text-ink">{t('shopping.sweep.confirm.body')}</p>
        <SheetError message={error} data-testid="shopping-sweep-error" />
        <Button
          ref={cancelRef}
          variant="secondary"
          size={52}
          data-testid="shopping-sweep-cancel"
          onClick={onClose}
          className="w-full"
        >
          {t('shopping.sweep.confirm.cancel')}
        </Button>
        <Button
          variant="danger"
          size={52}
          data-testid="shopping-sweep-confirm"
          onClick={onSubmit}
          className="w-full"
        >
          {t('shopping.sweep.confirm.submit')}
        </Button>
      </div>
    </BottomSheet>
  );
}
